// Minimal Drive client — docs-web fetches a file's bytes, saves new
// versions of it, renames it, and uploads Save-As copies. Full Drive CRUD
// lives in the drive-web SPA.

const baseUrl = import.meta.env.VITE_DRIVE_BFF_BASE_URL;

if (!baseUrl) {
    throw new Error("VITE_DRIVE_BFF_BASE_URL is required.");
}

export interface DriveItem {
    id: string;
    parentId: string | null;
    kind: "Folder" | "File";
    name: string;
    contentType: string | null;
    sizeBytes: number;
    createdAt: string;
    updatedAt: string;
    lastAccessedAt?: string | null;
    isTrashed?: boolean;
    isFavorite?: boolean;
    /** Set only when someone else owns the item and shared it with the caller. */
    ownerEmail?: string | null;
    /** The caller's access to a shared item: "viewer" | "editor". */
    role?: string | null;
    canDownload?: boolean | null;
}

/** A failed Drive call. `message` is the server's ProblemDetails text when it sent one. */
export class DriveApiError extends Error {
    readonly status: number;

    constructor(message: string, status: number) {
        super(message);
        this.name = "DriveApiError";
        this.status = status;
    }
}

export const VIEW_ONLY_SAVE_MESSAGE = "You have view access — use Save As to keep a copy.";

/**
 * A version upload to a file shared with the caller view-only comes back
 * 403 / 404 — say why instead of showing the bare status line.
 */
export function explainSaveError(err: unknown, role: string | null | undefined): unknown {
    if (role === "viewer" && err instanceof DriveApiError && (err.status === 403 || err.status === 404)) {
        return new Error(VIEW_ONLY_SAVE_MESSAGE);
    }
    return err;
}

async function failure(res: Response, what: string): Promise<DriveApiError> {
    let message = `${what} → ${res.status} ${res.statusText}`;
    try {
        const body = (await res.json()) as { title?: string; detail?: string };
        message = body.detail ?? body.title ?? message;
    } catch {
        /* not a JSON body — keep the status line */
    }
    return new DriveApiError(message, res.status);
}

/** True when `url` points at the Drive BFF — the only host we send the bearer token to. */
export function isDriveUrl(url: string): boolean {
    try {
        return new URL(url, window.location.href).origin === new URL(baseUrl).origin;
    } catch {
        return false;
    }
}

function authHeaders(token: string | undefined, accept?: string): Headers {
    const headers = new Headers();
    if (accept) headers.set("Accept", accept);
    if (token) headers.set("Authorization", `Bearer ${token}`);
    return headers;
}

function fileForm(blob: Blob, fileName: string): FormData {
    const form = new FormData();
    form.append("file", new File([blob], fileName, { type: blob.type || "application/octet-stream" }));
    return form;
}

export const driveApi = {
    listItems: async (
        parentId: string | null,
        token: string | undefined
    ): Promise<DriveItem[]> => {
        const url = new URL("/api/items", baseUrl);
        if (parentId) url.searchParams.set("parentId", parentId);
        const res = await fetch(url.toString(), { headers: authHeaders(token, "application/json"), credentials: "omit" });
        if (!res.ok) throw await failure(res, "Drive /api/items");
        return (await res.json()) as DriveItem[];
    },

    /** Files ordered by when the user last opened them (newest first). */
    listRecent: async (take: number, token: string | undefined): Promise<DriveItem[]> => {
        const url = new URL("/api/recent", baseUrl);
        url.searchParams.set("take", String(take));
        const res = await fetch(url.toString(), { headers: authHeaders(token, "application/json"), credentials: "omit" });
        if (!res.ok) throw await failure(res, "Drive /api/recent");
        return (await res.json()) as DriveItem[];
    },

    getItem: async (id: string, token: string | undefined): Promise<DriveItem> => {
        const res = await fetch(new URL(`/api/items/${encodeURIComponent(id)}`, baseUrl).toString(), {
            headers: authHeaders(token, "application/json"),
            credentials: "omit"
        });
        if (!res.ok) throw await failure(res, `Drive /api/items/${id}`);
        return (await res.json()) as DriveItem;
    },

    downloadBlob: async (id: string, token: string | undefined): Promise<Blob> => {
        const res = await fetch(
            new URL(`/api/files/${encodeURIComponent(id)}/download`, baseUrl).toString(),
            { headers: authHeaders(token), credentials: "omit" }
        );
        if (!res.ok) throw await failure(res, `Drive download ${id}`);
        return await res.blob();
    },

    /** Creates a NEW file. Drive auto-renames collisions to "name (2).ext". */
    uploadBlob: async (
        blob: Blob,
        fileName: string,
        parentId: string | null,
        token: string | undefined
    ): Promise<DriveItem> => {
        const form = fileForm(blob, fileName);
        if (parentId) form.append("parentId", parentId);
        const res = await fetch(new URL("/api/files", baseUrl).toString(), {
            method: "POST",
            headers: authHeaders(token, "application/json"),
            body: form,
            credentials: "omit"
        });
        if (!res.ok) throw await failure(res, "Drive upload");
        return (await res.json()) as DriveItem;
    },

    /** Saves new bytes as the next version of an existing file (same id, same name). */
    uploadVersion: async (
        fileId: string,
        blob: Blob,
        fileName: string,
        token: string | undefined
    ): Promise<DriveItem> => {
        const res = await fetch(
            new URL(`/api/files/${encodeURIComponent(fileId)}/versions`, baseUrl).toString(),
            {
                method: "POST",
                headers: authHeaders(token, "application/json"),
                body: fileForm(blob, fileName),
                credentials: "omit"
            }
        );
        if (!res.ok) throw await failure(res, "Drive save");
        return (await res.json()) as DriveItem;
    },

    /** Renames a file in place. 400 "Duplicate name" surfaces as the error message. */
    renameItem: async (id: string, name: string, token: string | undefined): Promise<DriveItem> => {
        const headers = authHeaders(token, "application/json");
        headers.set("Content-Type", "application/json");
        const res = await fetch(new URL(`/api/items/${encodeURIComponent(id)}`, baseUrl).toString(), {
            method: "PUT",
            headers,
            body: JSON.stringify({ name }),
            credentials: "omit"
        });
        if (!res.ok) throw await failure(res, "Drive rename");
        return (await res.json()) as DriveItem;
    }
};
